import { AHInput, AHText, Box, Card } from "arkh";
import { ColorSelect, ColorSelectItem } from "./ColorSelect";

import React, { useCallback, useEffect, useState } from "react";

import { useIntl } from "react-intl";
import { graphql, useMutation } from "react-relay";
import { TodoCategoryTagEditScreenMutation } from "./__generated__/TodoCategoryTagEditScreenMutation.graphql";
import { StoreAdminNavigationScreenProps } from "../StoreAdmin/StoreAdminNavigation";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

type TodoCategoryTagEditScreenProps =
  StoreAdminNavigationScreenProps<"TodoCategoryTagEdit">;

export function TodoCategoryTagEditScreen({
  route: { params },
  navigation,
}: TodoCategoryTagEditScreenProps) {
  const [deleteTagMutation,] = useMutation(graphql`
    mutation TodoCategoryTagEditScreenTagDeleteMutation(
      $categoryId: ID!
      $id: ID!
    ) {
      todoCategory(id: $categoryId) {
        tagDelete(input: { id: $id }) {
          todoCategory {
            id
            tags {
              id
            }
          }
        }
      }
    }
  `);
  const deleteTag = useCallback(() => {
    deleteTagMutation({
      variables: {
        categoryId: params.categoryId,
        id: params.tag.id,
      },
      onCompleted: () => {
        navigation.goBack();
      },
    });
  }, [deleteTagMutation, navigation, params.categoryId, params.tag.id]);
  const intl = useIntl();

  const remoteColor = params.tag.color;
  const remoteTitle = params.tag.title;

  const [color, setColor] = useState(remoteColor);
  const [title, setTitle] = useState(remoteTitle);

  useEffect(() => {
    setTitle(remoteTitle);
  }, [remoteTitle]);

  useEffect(() => {
    setColor(remoteColor);
  }, [remoteColor]);

  const isDirty = color !== remoteColor || title !== remoteTitle;

  const [updateMutation, updateLoading] =
    useMutation<TodoCategoryTagEditScreenMutation>(graphql`
      mutation TodoCategoryTagEditScreenMutation(
        $categoryId: ID!
        $updateTitleInput: TagUpdateTitleInput!
        $updateColorInput: TagUpdateColorInput!
      ) {
        todoCategory(id: $categoryId) {
          tagUpdateTitle(input: $updateTitleInput) {
            todoCategory {
              id
            }
          }
        }
        todoCategory(id: $categoryId) {
          tagUpdateColor(input: $updateColorInput) {
            todoCategory {
              id
              tags {
                id
                color
                title
              }
            }
          }
        }
      }
    `);

  const update = useCallback(() => {
    updateMutation({
      variables: {
        categoryId: params.categoryId,
        updateTitleInput: { id: params.tag.id, title },
        updateColorInput: { id: params.tag.id, color },
      },
      onCompleted: navigation.goBack,
    });
  }, [
    updateMutation,
    navigation.goBack,
    params.categoryId,
    params.tag.id,
    color,
    title,
  ]);

  useNavigationHeader({
    type: "SAVE",
    onCancel: navigation.goBack,
    onSave: update,
    saveLoading: updateLoading,
    disabled: !isDirty,
    more: [
      {
        label: intl.formatMessage({
          defaultMessage: "Delete Tag",
          description: "Tag Edit delete tag button title",
        }),
        onPress: deleteTag,
        icon: "trash",
        color: "negative",
        type: "button",
      },
    ],
  });
  const intl_tokens = useIntlTokens();
  return (
    <KeyboardAwareScrollView
      contentInsetAdjustmentBehavior="automatic"
      keyboardShouldPersistTaps="handled"
    >
      <Box
        space="medium"
        style={{
          flex: 1,
        }}
      >
        <Box spaceBottom="large">
          <AHText type="title-small" spaceBottom="small" spaceLeft="xsmall">
            {intl_tokens.Name}
          </AHText>
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
              borderWidth: 1,
            }}
            spaceLeft="medium"
            color="surface"
            cornerRadius="large"
            borderColor="border-primary"
          >
            <ColorSelectItem color={color} selected={false} />
            <AHInput
              placeholder={intl.formatMessage({
                defaultMessage: "Tag title",
                description: "tag title input placeholder",
              })}
              textType="list-item-title"
              defaultStyle
              autoFocus
              style={{ flex: 1, borderWidth: 0 }}
              value={title}
              onChangeText={setTitle}
            />
          </Box>
        </Box>
        <AHText type="title-small" spaceBottom="small" spaceLeft="xsmall">
          {intl_tokens.Color}
        </AHText>
        <Card defaultBorder>
          <ColorSelect selected={color} setSelected={setColor} />
        </Card>
      </Box>
    </KeyboardAwareScrollView>
  );
}
